import { Box, Card, Chip, IconButton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useMemo } from 'react'
import { BsTrash } from 'react-icons/bs'

import type { Question } from '@/types'

interface QuestionCardMiniProps {
  question: Question
  handleToggleQuestion: (question: Question) => void
}

export const QuestionCardMini = memo<QuestionCardMiniProps>(
  ({ question, handleToggleQuestion }) => {
    const theme = useTheme()

    const difficultyConfig = useMemo(() => {
      const configs = {
        easy: {
          color: theme.palette.success.main,
          label: 'Fácil',
          chipColor: 'success' as const,
        },
        intermediary: {
          color: theme.palette.warning.main,
          label: 'Médio',
          chipColor: 'warning' as const,
        },
        hard: {
          color: theme.palette.error.main,
          label: 'Difícil',
          chipColor: 'error' as const,
        },
      }

      return (
        configs[question.difficulty as keyof typeof configs] || {
          color: theme.palette.text.secondary,
          label: 'Sem definição',
          chipColor: 'default' as const,
        }
      )
    }, [question.difficulty, theme])

    return (
      <Card
        data-qa="question-card-mini"
        elevation={2}
        sx={{
          width: '100%',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          borderRadius: 2,
          borderLeft: `4px solid ${difficultyConfig.color}`,
          '&:hover': {
            boxShadow: theme.shadows[6],
          },
          transition: theme.transitions.create(['box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
        }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.4,
                color: 'text.primary',
              }}>
              {question.question}
            </Typography>
          </Box>

          <IconButton
            aria-label="delete"
            size="small"
            onClick={() => handleToggleQuestion(question)}
            sx={{
              ml: 1,
              color: theme.palette.grey[500],
              '&:hover': {
                color: theme.palette.error.main,
              },
            }}>
            <BsTrash fontSize="small" />
          </IconButton>
        </Box>

        <Chip
          label={difficultyConfig.label}
          size="small"
          color={difficultyConfig.chipColor}
          variant="filled"
          sx={{ fontWeight: 600 }}
        />

        {question.tags.length > 0 && (
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, mb: 1 }}>
              Tags ({question.tags.length}):
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={0.5}>
              {question.tags.slice(0, 6).map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: 1.5,
                    color: 'text.secondary',
                    borderColor: theme.palette.divider,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                />
              ))}
              {question.tags.length > 6 && (
                <Chip
                  label={`+${question.tags.length - 6}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: 1.5,
                    color: 'text.secondary',
                    borderColor: theme.palette.divider,
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </Box>
        )}

        <Stack direction="column">
          <Typography variant="caption" color="text.secondary">
            {question.type === 'multiple choice' ? 'Múltipla escolha' : 'Discursiva'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Por: {question.teacher.name}
          </Typography>
        </Stack>
      </Card>
    )
  },
)

QuestionCardMini.displayName = 'QuestionCardMini'
